import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { TutorialOverlay } from './TutorialOverlay';
import { STEPS, TUTORIAL_STORAGE_KEY, type DemoAction, type TutorialStep } from './steps';

interface State {
  active: boolean;
  stepIndex: number;
  actionsLog: DemoAction[];
  wrongActionTick: number;
  targetVersion: number;
}

type ReducerAction =
  | { type: 'start' }
  | { type: 'skip' }
  | { type: 'next' }
  | { type: 'demo-action'; action: DemoAction }
  | { type: 'wrong-action' }
  | { type: 'target-registered' };

const INITIAL: State = {
  active: false,
  stepIndex: 0,
  actionsLog: [],
  wrongActionTick: 0,
  targetVersion: 0,
};

function tailMatches(log: DemoAction[], expected: DemoAction[]): boolean {
  if (log.length < expected.length) return false;
  const tail = log.slice(-expected.length);
  return tail.every((a, i) => a === expected[i]);
}

function reducer(state: State, action: ReducerAction): State {
  switch (action.type) {
    case 'start':
      return { ...INITIAL, active: true };
    case 'skip':
      return { ...state, active: false };
    case 'next': {
      const next = state.stepIndex + 1;
      if (next >= STEPS.length) {
        return { ...state, active: false };
      }
      return { ...state, stepIndex: next, actionsLog: [] };
    }
    case 'demo-action': {
      const step = STEPS[state.stepIndex];
      if (step.kind !== 'demo-sticker') return state;
      const newLog = [...state.actionsLog, action.action];
      if (tailMatches(newLog, step.expect)) {
        const next = state.stepIndex + 1;
        if (next >= STEPS.length) {
          return { ...state, active: false };
        }
        return { ...state, stepIndex: next, actionsLog: [] };
      }
      return { ...state, actionsLog: newLog };
    }
    case 'wrong-action':
      return { ...state, wrongActionTick: state.wrongActionTick + 1 };
    case 'target-registered':
      return { ...state, targetVersion: state.targetVersion + 1 };
    default:
      return state;
  }
}

interface TutorialContextValue {
  state: State;
  currentStep: TutorialStep;
  start: () => void;
  skip: () => void;
  next: () => void;
  demoAction: (action: DemoAction) => void;
  signalWrongAction: () => void;
  registerTarget: (id: string, el: HTMLElement | null) => void;
  getTarget: (id: string) => HTMLElement | null;
}

const TutorialContext = createContext<TutorialContextValue | null>(null);

interface ProviderProps {
  children: React.ReactNode;
}

export function TutorialProvider({ children }: ProviderProps) {
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const targetsRef = useRef<Map<string, HTMLElement>>(new Map());

  const start = useCallback(() => dispatch({ type: 'start' }), []);
  const skip = useCallback(() => dispatch({ type: 'skip' }), []);
  const next = useCallback(() => dispatch({ type: 'next' }), []);
  const demoAction = useCallback(
    (action: DemoAction) => dispatch({ type: 'demo-action', action }),
    []
  );
  const signalWrongAction = useCallback(() => dispatch({ type: 'wrong-action' }), []);

  const registerTarget = useCallback((id: string, el: HTMLElement | null) => {
    if (el) {
      targetsRef.current.set(id, el);
      dispatch({ type: 'target-registered' });
    } else {
      targetsRef.current.delete(id);
    }
  }, []);

  const getTarget = useCallback((id: string) => targetsRef.current.get(id) ?? null, []);

  useEffect(() => {
    if (!state.active) {
      try {
        localStorage.setItem(TUTORIAL_STORAGE_KEY, '1');
      } catch {
        /* ignore */
      }
    }
  }, [state.active]);

  const value = useMemo<TutorialContextValue>(
    () => ({
      state,
      currentStep: STEPS[state.stepIndex],
      start,
      skip,
      next,
      demoAction,
      signalWrongAction,
      registerTarget,
      getTarget,
    }),
    [state, start, skip, next, demoAction, signalWrongAction, registerTarget, getTarget]
  );

  return (
    <TutorialContext.Provider value={value}>
      {children}
      {state.active && <TutorialOverlay />}
    </TutorialContext.Provider>
  );
}

export function useTutorial() {
  const ctx = useContext(TutorialContext);
  if (!ctx) {
    throw new Error('useTutorial deve ser usado dentro de <TutorialProvider>');
  }
  return ctx;
}
